import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// Shared hook: exposes installability + a trigger.
export function useInstall() {
  const [canInstall, setCanInstall] = useState(!!window.__deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    const dp = window.__deferredPrompt;
    if (dp) {
      dp.prompt();
      const { outcome } = await dp.userChoice;
      if (outcome === "accepted") toast.success("Installation en cours…");
      window.__deferredPrompt = null;
      setCanInstall(false);
    } else if (isIOS()) {
      setIosOpen(true);
    } else {
      toast.info("Utilisez le menu de votre navigateur → « Installer l'application ».");
    }
  };

  const IosGuide = (
    <Dialog open={iosOpen} onOpenChange={setIosOpen}>
      <DialogContent className="max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2"><Smartphone size={20} className="text-tp-green" /> Installer sur iPhone</DialogTitle>
          <DialogDescription>Ajoutez Tailleur Pro à votre écran d'accueil en 2 étapes :</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 pt-1">
          <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-tp-green text-white flex items-center justify-center font-bold text-sm shrink-0">1</span>
            <span className="text-base text-tp-ink flex items-center gap-1">Appuyez sur <Share size={18} className="text-tp-green" /> <b>Partager</b> en bas de Safari.</span></li>
          <li className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-tp-green text-white flex items-center justify-center font-bold text-sm shrink-0">2</span>
            <span className="text-base text-tp-ink flex items-center gap-1">Choisissez <Plus size={18} className="text-tp-green" /> <b>Sur l'écran d'accueil</b>.</span></li>
        </ol>
      </DialogContent>
    </Dialog>
  );

  const available = !installed && (canInstall || isIOS());
  return { available, installed, promptInstall, IosGuide };
}

export function InstallBanner() {
  const { available, promptInstall, IosGuide } = useInstall();
  const [dismissed, setDismissed] = useState(localStorage.getItem("tp_install_dismissed") === "1");

  if (!available || dismissed) return <>{IosGuide}</>;

  return (
    <>
      <div data-testid="install-banner" className="mt-3 w-full flex items-center gap-3 bg-tp-ink text-white rounded-xl px-4 py-3">
        <div className="w-9 h-9 rounded-lg bg-tp-gold flex items-center justify-center shrink-0">
          <Download size={18} className="text-tp-ink" />
        </div>
        <button data-testid="install-app-btn" onClick={promptInstall} className="flex-1 text-left">
          <p className="font-semibold text-sm">Installer l'application</p>
          <p className="text-white/70 text-xs">Accès rapide depuis votre écran d'accueil</p>
        </button>
        <button data-testid="install-dismiss" onClick={() => { setDismissed(true); localStorage.setItem("tp_install_dismissed", "1"); }} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
          <X size={16} />
        </button>
      </div>
      {IosGuide}
    </>
  );
}
